import React from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import Select, { SingleValue } from 'react-select'
import { useAtomValue, useSetAtom } from 'jotai'
import styled from 'styled-components'
import dt from 'dayjs'
import Decimal from 'decimal.js'
import * as lib from '../../lib'
import * as state from '../../states'
import {
	ERoleAccess,
	type IAccount,
	type IAccountBuyer,
	type IOfferDropDown,
	type IOfferSpend,
	type TOfferSpendHistory
} from '../../types'
import {
	accountOptionValue,
	accountOptionLabel,
	accountFormatOptionLabel,
	offerOptionValue,
	offerOptionLabel,
	offerFormatOptionLabel,
	offerSpendConditions
} from './helpers'
import * as helpers from '../../helpers'
import { reactSelectStyles } from '../filters'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IOfferSpendEditModalProps = {
	item: IOfferSpend
	show: boolean
	accounts: Array<IAccountBuyer> | null
	offers: Array<IOfferDropDown> | null
	onClose: () => void
	onSave: () => void
}

const MyControl = styled(Form.Control)`
    display: inline-flex;
    height: unset;
    padding: 2px 4px !important;
    width: 16em;
`

const MyCheck = styled(Form.Check)`
    display: inline-flex;
`

export const OfferSpendEditModal = (
	props: IOfferSpendEditModalProps
) => {
	const { t } = useTranslation()
	const isThemeDark = useAtomValue(state.themeIsDarkAtom)
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const profile = useAtomValue(state.profileAtom)
	const $access = new lib.Access(profile ?? null)

	const {
		item,
		accounts: allAccounts,
		offers: allOffers,
		show,
		onClose,
		onSave
	} = props

	const today = React.useMemo(() => dt.utc(new Date()).startOf('day'), [])

	const id = item?.id ?? 0
	const [offerId, setOfferId] = React.useState<number>(item.offer?.id ?? 0)
	const [accountId, setAccountId] = React.useState<number>(item.account?.id ?? 0)
	const [at, setAt] = React.useState<Date>(dt.utc(item.at).toDate())
	const [amountInput, setAmountInput] = React.useState<string>(helpers.formatAmount(item.amount))
	const history = React.useMemo<TOfferSpendHistory[]>(() => item?.history ?? [], [item?.history])
	const [notes, setNotes] = React.useState<string>(item.notes ?? '')

	const accountInGroup = React.useMemo(() => {
		if (allAccounts && accountId) {
			const account = allAccounts?.find(_ => _.id === accountId) ?? null
			const access = new lib.Access(account as IAccount)
			return access.inGroup()
		}
		return null
	}, [allAccounts, accountId])
	const inGroup = $access.inGroup()
	const offersInGroup = React.useMemo(() => {
		if (allOffers && accountId && accountInGroup) {
			return allOffers.filter(_ => _.group === accountInGroup)
		} else if (allOffers && !accountId && inGroup) {
			return allOffers.filter(_ => _.group === inGroup)
		}
		return allOffers
	}, [allOffers, inGroup, accountId, accountInGroup])

	if (!show) {
		return null
	}

	const onUpdate = async () => {
		const uri: string = `offer/spend/${id}`
		const method: string = 'PATCH'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(tokens?.access)
		try {
			const body: string = JSON.stringify({
				offerId,
				accountId,
				at: dt.utc(at)?.format('YYYY-MM-DD') ?? null,
				amount: !!amountInput ? new Decimal(amountInput).toNumber() : 0,
				notes
			})
			const options: lib.FetchOptions = { body, method, params, headers }
			setFetchIsLoading(state.turnOnLoader)
			await lib.fetching<void>(uri, options)
			onSave()
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	const {
		today: _today,
		threeDaysBeforeToday,
		hasOfferSpendAnyDay,
		isBetweenAvailableRange,
		disabled
	} = offerSpendConditions(at, $access)

	const modalTitle = `${t(ALL_LANG_KEY.offer_spend_crud_modal.title_edit)} #${item?.id}`

	return <>
		<Modal show={show} onHide={onClose} className={'scroll'}>
			<Modal.Header closeButton>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Form>
					<Form.Group>

						<Form.Label className="text-dark" htmlFor="select-offer">{`${t(ALL_LANG_KEY.offer_spend_crud_modal.field_offer_label)}`}</Form.Label>
						<Select
							inputId="select-offer"
							className="react-select-container"
							options={offersInGroup ?? []}
							value={offersInGroup?.find(_ => _.id === offerId) ?? null}
							placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
							noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
							getOptionValue={offerOptionValue}
							getOptionLabel={offerOptionLabel}
							formatOptionLabel={offerFormatOptionLabel}
							styles={reactSelectStyles<IOfferDropDown, false>(isThemeDark)}
							onChange={(_: SingleValue<IOfferDropDown>) => setOfferId(_?.id ?? 0)}
							isDisabled={disabled}
							isSearchable={true}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor="select-account">{`${t(ALL_LANG_KEY.offer_spend_crud_modal.field_user_label)}`}</Form.Label>
						<Select
							inputId="select-account"
							className="react-select-container"
							options={allAccounts ?? []}
							value={allAccounts?.find(_ => _.id === accountId) ?? null}
							placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
							noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
							getOptionValue={accountOptionValue}
							getOptionLabel={(data) => accountOptionLabel(data, t)}
							formatOptionLabel={(data) => accountFormatOptionLabel(data, t)}
							styles={reactSelectStyles<IAccountBuyer, false>(isThemeDark)}
							onChange={(_: SingleValue<IAccountBuyer>) => setAccountId(_?.id ?? 0)}
							isDisabled={!$access.$(ERoleAccess.OfferSpendAnyAccount)}
							isSearchable={true}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor="input-at">{`${t(ALL_LANG_KEY.offer_spend_crud_modal.field_date_label)}`}</Form.Label>
						<Form.Control
							id="input-at"
							placeholder={`${t(ALL_LANG_KEY.offer_spend_crud_modal.field_date_placeholder)}`}
							type="date"
							value={dt.utc(at).format('YYYY-MM-DD')}
							{...(!hasOfferSpendAnyDay ? {
								min: dt.utc(threeDaysBeforeToday).format('YYYY-MM-DD'),
								max: dt.utc(today).format('YYYY-MM-DD')
							} : {})}
							disabled={false}
							onChange={
								(e) => {
									const v = dt.utc(e.target.value)
									v.isValid() && setAt(v.startOf('day').toDate())
								}
							}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor="input-amount">{`${t(ALL_LANG_KEY.offer_spend_crud_modal.field_amount_label)}`}</Form.Label>
						<Form.Control
							id="input-amount"
							placeholder={`${t(ALL_LANG_KEY.offer_spend_crud_modal.field_amount_placeholder)}`}
							type="number"
							step={0.0001}
							value={amountInput}
							disabled={false}
							onChange={(e) => setAmountInput(e.target.value)}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-notes'}>{`${t(ALL_LANG_KEY.offer_spend_crud_modal.field_notes_label)}`}</Form.Label>
						<Form.Control
							id={'input-notes'}
							placeholder={`${t(ALL_LANG_KEY.offer_spend_crud_modal.field_notes_placeholder)}`}
							type="text"
							multiple={true}
							disabled={false}
							value={notes ?? ''}
							onChange={(e) => setNotes(e.target.value)}
						/>
						<br/>

					</Form.Group>
				</Form>
			</Modal.Body>

			<Modal.Footer>
				<Button variant="primary" onClick={onUpdate}>{`${t(ALL_LANG_KEY.offer_spend_crud_modal.btn_update)}`}</Button>
				<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.offer_spend_crud_modal.btn_close)}`}</Button>
			</Modal.Footer>
		</Modal>
	</>
}
